import { type FormEvent, useState } from 'react'
import { Link } from 'react-router'

import { useAuth } from '@langmail/shared-ui'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

// Sign-in for returning standalone users: their @langmail.me address + password.
// On success the session updates and the auth page's effect routes into the app.
export const StandaloneSignInForm = () => {
  const { signInWithLangmail } = useAuth()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setError(null)
    setSubmitting(true)
    const { error: signInError } = await signInWithLangmail({ email: email.trim(), password })
    if (signInError) {
      setError(signInError.message)
      setSubmitting(false)
    }
  }

  return (
    <form className="mt-6 space-y-4 text-left" onSubmit={handleSubmit}>
      <div className="space-y-1.5">
        <Label htmlFor="signin-email">Langmail address</Label>
        <Input
          autoCapitalize="none"
          autoComplete="username"
          autoCorrect="off"
          id="signin-email"
          onChange={(event) => setEmail(event.target.value)}
          placeholder="alice@langmail.me"
          spellCheck={false}
          type="email"
          value={email}
        />
      </div>

      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <Label htmlFor="signin-password">Password</Label>
          <Link className="text-muted-foreground text-xs underline hover:text-foreground" to="/forgot-password">
            Forgot password?
          </Link>
        </div>
        <Input
          autoComplete="current-password"
          id="signin-password"
          onChange={(event) => setPassword(event.target.value)}
          type="password"
          value={password}
        />
      </div>

      {error ? <p className="text-destructive text-sm">{error}</p> : null}

      <Button
        className="w-full rounded-lg bg-primary px-8 py-4 text-[15px] text-primary-foreground hover:bg-primary/90"
        disabled={submitting || email.trim().length === 0 || password.length === 0}
        type="submit"
      >
        {submitting ? 'Signing in…' : 'Sign in'}
      </Button>
    </form>
  )
}
